import React, { CSSProperties, useState } from 'react'
import useMediaQuery from '@material-ui/core/useMediaQuery'
import useTheme from '@material-ui/core/styles/useTheme'
import MUIDialog from '@material-ui/core/Dialog'
import DialogTitle from '@material-ui/core/DialogTitle'
import DialogContent from '@material-ui/core/DialogContent'
import DialogActions from '@material-ui/core/DialogActions'
import Button, { ButtonProps } from '@material-ui/core/Button'
import Box from '@material-ui/core/Box'
import LinearProgress from '@material-ui/core/LinearProgress'
import Typography from '@material-ui/core/Typography'
import { Grid, InputLabel, Tab, Tabs, IconButton } from '@material-ui/core'
import InfoIcon from '@material-ui/icons/InfoOutlined'
import CloseIcon from '@material-ui/icons/Close'

import { useStyles } from './styles'

const toKebabCase = (str: string): string => {
  return str
    .toLowerCase()
    .replace(/\s+/g, '-')
    .replace(/[^a-z0-9-]/g, '')
}

interface DialogProps {
  isOpen: boolean
  isEdit?: boolean
  title: string
  subtitle?: string
  contentStyle?: CSSProperties
  style?: CSSProperties
  className?: string
  contentClassName?: string
  showActions?: boolean
  okButtonText?: string
  okButtonProps?: ButtonProps
  cancelButtonText?: string
  hideCancelButton?: boolean
  onAccept?: () => void
  onCancel: () => void
  isLoading?: boolean
  overflow?: boolean
  readOnly?: boolean
  tabs?: string[]
  tabIndex?: number
  setTabIndex?: (index: number) => void
  showTabsActions?: boolean
  showUserCount?: boolean
  isRequestingData?: boolean
  userCount?: number
  userCountExtra?: React.ReactNode
  maxWidth?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | false
}

const actionsButtonStyle: React.CSSProperties = {
  borderRadius: '5px',
  fontSize: '14px',
  fontWeight: 400,
  fontFamily: 'Montserrat',
  lineHeight: '100%',
  letterSpacing: 0,
  minWidth: '110px',
  minHeight: '40px',
  textTransform: 'capitalize',
}

const Dialog: React.FC<DialogProps> = ({
  isOpen,
  isEdit,
  onCancel,
  title,
  subtitle,
  children,
  showActions = false,
  okButtonText,
  okButtonProps,
  cancelButtonText,
  hideCancelButton = false,
  onAccept = () => undefined,
  isLoading,
  contentStyle,
  style,
  className,
  contentClassName,
  overflow,
  readOnly,
  tabs,
  tabIndex,
  setTabIndex,
  showTabsActions = false,
  showUserCount = false,
  isRequestingData = false,
  userCount = 0,
  userCountExtra = null,
  maxWidth = 'sm',
}) => {
  const theme = useTheme()
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'))
  const classes = useStyles()
  const paperClassName = overflow ? classes.overflow : ''
  const [tabsEnabled] = useState<boolean>(tabs !== undefined)
  const kebabTitle = toKebabCase(title || 'dialog')
  const isLastTab = tabs ? tabIndex === tabs.length - 1 : false
  const showAcceptButton = (isEdit && !isLastTab) || (!readOnly && tabIndex === 0)

  return (
    <MUIDialog
      fullScreen={fullScreen}
      open={isOpen}
      onClose={onCancel}
      closeAfterTransition
      maxWidth={maxWidth}
      PaperProps={{ className: `${classes.dialog} ${paperClassName} ${className ?? ''}`, style }}
    >
      <DialogTitle disableTypography className={classes.dialogTitle}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6" className={classes.title}>
            {title}
          </Typography>
          <IconButton aria-label="close" onClick={onCancel} style={{ padding: 8 }}>
            <CloseIcon style={{ color: 'black' }} />
          </IconButton>
        </Box>
      </DialogTitle>

      {tabsEnabled && (
        <Tabs
          value={tabIndex}
          onChange={(_, newValue) => setTabIndex?.(newValue)}
          indicatorColor="primary"
          textColor="primary"
          variant="standard"
          scrollButtons="auto"
          style={{
            padding: '0px 24px',
          }}
        >
          {tabs?.map((label, idx) => (
            <Tab
              key={label}
              label={label}
              id={`${kebabTitle}-tab-${idx}`}
              aria-controls={`${kebabTitle}-tabpanel-${idx}`}
              style={{
                fontSize: '14px',
                fontWeight: 700,
                fontFamily: 'Montserrat',
                lineHeight: '100%',
                letterSpacing: 0,
                textTransform: 'none',
                paddingLeft: 0,
                paddingRight: 0,
                minWidth: '138px',
              }}
            />
          ))}
        </Tabs>
      )}

      <DialogContent
        dividers
        style={{
          borderBottom: 'none',
          padding: tabsEnabled ? '10px 24px 0px 24px' : '30px 35px',
          borderTop: tabsEnabled ? 'none' : '1px solid rgba(0, 0, 0, 0.12)',
          maxHeight: 'calc(100vh - 200px)',
          overflowY: 'auto',
          ...contentStyle,
        }}
        className={`${classes.overflow} ${classes.dialogContent} ${contentClassName ?? ''}`}
      >
        <>
          {subtitle && ( // Solo se muestra si `subtitle` está definido
            <Typography variant="subtitle1" className={classes.subtitle}>
              {subtitle}
            </Typography>
          )}
          {children}
        </>
      </DialogContent>

      {isLoading && (
        <Box style={{ padding: '0 24px 16px' }}>
          <LinearProgress />
        </Box>
      )}

      {showUserCount && (
        <Grid item xs={12}>
          <InputLabel style={{ padding: '0px 24px 15px' }}>
            {isRequestingData ? (
              <LinearProgress style={{ marginTop: 36, marginBottom: 37 }} />
            ) : (
              <div className={classes.userCountBox}>
                <InfoIcon style={{ width: 25, height: 25, marginRight: 8 }} />
                Esta acción impactará a {userCount} usuarios.
              </div>
            )}
          </InputLabel>
          {userCountExtra}
        </Grid>
      )}

      {showActions && (
        <DialogActions
          className={classes.actionsContainer}
          style={{ padding: '0px 35px 30px 35px' }}
        >
          {!hideCancelButton && (
            <Button
              onClick={onCancel}
              className={classes.actionsButton}
              style={{
                borderRadius: '10px',
                fontSize: '16px',
                fontWeight: 400,
                fontFamily: 'Arial',
                lineHeight: '18.4px',
                letterSpacing: 0,
                padding: '16px 45px',
              }}
            >
              {cancelButtonText ?? (readOnly ? 'Cerrar' : 'Cancelar')}
            </Button>
          )}
          {!readOnly && (
            <Button
              onClick={onAccept}
              variant="contained"
              color="primary"
              className={classes.actionsButton}
              style={{
                borderRadius: '10px',
                fontSize: '16px',
                fontWeight: 400,
                fontFamily: 'Arial',
                lineHeight: '18.4px',
                letterSpacing: 0,
                padding: '16px 45px',
              }}
              autoFocus
              {...okButtonProps}
            >
              {okButtonText ?? 'Aceptar'}
            </Button>
          )}
        </DialogActions>
      )}
      {showTabsActions && (
        <DialogActions
          disableSpacing
          className={classes.actionsContainer}
          style={{ padding: '0px 24px 24px' }}
        >
          <Grid container spacing={3}>
            <Grid item xs={3}>
              {showAcceptButton && (
                <Button
                  onClick={onAccept}
                  variant="contained"
                  color="primary"
                  style={actionsButtonStyle}
                  autoFocus
                  {...okButtonProps}
                >
                  {okButtonText ?? 'Aceptar'}
                </Button>
              )}
            </Grid>
            <Grid item xs={9} style={{ textAlign: 'right' }}>
              {tabIndex !== 0 && (
                <Button
                  onClick={() => setTabIndex?.(tabIndex! - 1)}
                  variant="outlined"
                  color="primary"
                  style={{ ...actionsButtonStyle, marginRight: '10px' }}
                >
                  Anterior
                </Button>
              )}
              {!isLastTab && (
                <Button
                  onClick={() => setTabIndex?.(tabIndex! + 1)}
                  variant="contained"
                  color="primary"
                  style={actionsButtonStyle}
                >
                  Siguiente
                </Button>
              )}
              {isLastTab && (
                <Button
                  onClick={onAccept}
                  variant="contained"
                  color="primary"
                  style={actionsButtonStyle}
                  autoFocus
                  {...okButtonProps}
                >
                  {okButtonText ?? 'Aceptar'}
                </Button>
              )}
            </Grid>
          </Grid>
        </DialogActions>
      )}
    </MUIDialog>
  )
}

Dialog.defaultProps = {
  subtitle: '',
  contentStyle: {},
  style: {},
  className: '',
  contentClassName: '',
  showActions: false,
  okButtonText: '',
  okButtonProps: {},
  cancelButtonText: '',
  hideCancelButton: false,
  isLoading: false,
  overflow: false,
  readOnly: false,
  onAccept: () => undefined,
  tabs: undefined,
  tabIndex: undefined,
  setTabIndex: undefined,
  showTabsActions: false,
  showUserCount: false,
  isRequestingData: false,
  userCount: 0,
  userCountExtra: null,
  isEdit: false,
  maxWidth: 'sm',
}

export { Dialog }
